/**
 * PaginaRedefinirSenha - Página para redefinir a senha usando token
 * 
 * CONTEXTO DE NEGÓCIO:
 * Página acessada através do link enviado por email para
 * permitir que o usuário crie uma nova senha.
 * 
 * FLUXO:
 * 1. Usuário acessa via link com token
 * 2. Sistema valida o token
 * 3. Usuário insere nova senha
 * 4. Sistema atualiza a senha e redireciona para login
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { clienteApi } from '../servicos/clienteApi';

export function PaginaRedefinirSenha() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [estaCarregando, setEstaCarregando] = useState(false);
  const [validandoToken, setValidandoToken] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Validar token ao carregar a página
  useEffect(() => {
    const validarToken = async () => {
      if (!token) {
        setTokenValido(false);
        setValidandoToken(false);
        return;
      }

      try {
        const response = await clienteApi.get(`/api/core/usuarios/validar-token-recuperacao/${token}`);
        if (response.data.valido) {
          setTokenValido(true);
          setTokenEmail(response.data.email || '');
        } else {
          setTokenValido(false);
        }
      } catch {
        setTokenValido(false);
      } finally {
        setValidandoToken(false);
      }
    };

    validarToken();
  }, [token]);

  const validarSenha = (senha: string): string | null => {
    if (senha.length < 8) {
      return 'A senha deve ter pelo menos 8 caracteres';
    }
    if (!/[A-Z]/.test(senha)) {
      return 'A senha deve conter pelo menos 1 letra maiúscula';
    }
    if (!/[a-z]/.test(senha)) {
      return 'A senha deve conter pelo menos 1 letra minúscula';
    }
    if (!/\d/.test(senha)) {
      return 'A senha deve conter pelo menos 1 número';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    // Validar senhas
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não coincidem');
      return;
    }

    const erroValidacao = validarSenha(novaSenha);
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    setEstaCarregando(true);

    try {
      await clienteApi.post('/api/core/usuarios/redefinir-senha', {
        token,
        nova_senha: novaSenha,
      });
      setSucesso(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setErro(error.response?.data?.detail || 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setEstaCarregando(false);
    }
  };

  // Loading enquanto valida token
  if (validandoToken) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary-600" />
          <p className="mt-4 text-gray-600">Validando link de recuperação...</p>
        </div>
      </div>
    );
  }

  // Token inválido ou expirado
  if (!tokenValido) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
        <div className="w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Link Inválido</h1>
            <p className="mt-2 text-gray-600">
              Este link de recuperação é inválido ou já expirou.
            </p>
          </div>

          <div className="space-y-3">
            <Link
              to="/recuperar-senha"
              className="block w-full rounded-md bg-primary-600 px-4 py-2 text-center text-white hover:bg-primary-700"
            >
              Solicitar novo link
            </Link>
            
            <Link
              to="/login"
              className="block w-full rounded-md border border-gray-300 px-4 py-2 text-center text-gray-700 hover:bg-gray-50"
            >
              Voltar para o Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Sucesso ao redefinir senha
  if (sucesso) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
        <div className="w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Senha Alterada!</h1>
            <p className="mt-2 text-gray-600">
              Sua senha foi redefinida com sucesso. Você já pode fazer login com sua nova senha.
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
          >
            Ir para o Login
          </button>
        </div>
      </div>
    );
  }

  // Formulário de nova senha
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
      <div className="w-full rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <Lock className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Nova Senha</h1>
          <p className="mt-2 text-gray-600">
            Crie uma nova senha para <strong>{tokenEmail}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {erro && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {erro}
            </div>
          )}

          <div>
            <label htmlFor="novaSenha" className="mb-1 block text-sm font-medium text-gray-700">
              Nova Senha
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                <Lock className="h-5 w-5" />
              </span>
              <input
                type={mostrarSenha ? 'text' : 'password'}
                id="novaSenha"
                value={novaSenha}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNovaSenha(e.target.value)}
                required
                minLength={8}
                placeholder="Mínimo 8 caracteres"
                className="block w-full rounded-md border py-2 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {mostrarSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Mínimo 8 caracteres, 1 maiúscula, 1 minúscula e 1 número
            </p>
          </div>

          <div>
            <label htmlFor="confirmarSenha" className="mb-1 block text-sm font-medium text-gray-700">
              Confirmar Senha
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                <Lock className="h-5 w-5" />
              </span>
              <input
                type={mostrarSenha ? 'text' : 'password'}
                id="confirmarSenha"
                value={confirmarSenha}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmarSenha(e.target.value)}
                required
                placeholder="Digite a senha novamente"
                className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={estaCarregando || !novaSenha || !confirmarSenha}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {estaCarregando ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Redefinindo...
              </>
            ) : (
              'Redefinir Senha'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para o Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PaginaRedefinirSenha;
